import { computed, type Ref } from 'vue'
import type { ActiveHeadEntry, UseHeadOptions, Meta } from '@unhead/vue'
import { useHead } from '@unhead/vue'
import { useI18n } from 'vue-i18n'
import { useRobotsPlugin } from '@/plugins/robots'
import type { RouteLocationRaw } from 'vue-router'
import { useRouter } from 'vue-router'
import { useSegmentPage } from './gtm/useSegmentPage'
import { useTranslator } from '@/i18n'

type Maybe<T> = T | undefined
type MaybeFunction<T> = T | (() => T)

export function useMeta<
    T extends {
        title: () => Maybe<string>
        description?: MaybeFunction<Maybe<string>>
        og?: {
            title?: MaybeFunction<Maybe<string>>
            description?: MaybeFunction<Maybe<string>>
            type: MaybeFunction<Maybe<string>>
            url: MaybeFunction<Maybe<string | RouteLocationRaw>>
            image?: MaybeFunction<Maybe<string>>
            site_name?: MaybeFunction<Maybe<string>>
        }
        article?: {
            published_time: MaybeFunction<Maybe<Date>>
            author: MaybeFunction<Maybe<string>>
        }
    },
>(
    input: T,
    data: Ref<unknown>,
    fetching: Ref<boolean>,
    stale: Ref<boolean>,
    options?: UseHeadOptions
): ActiveHeadEntry<T> | void {
    const robots = useRobotsPlugin()
    const router = useRouter()

    const { t } = useI18n()
    const globalDefault = computed(() => t('globals.default_meta_title'))

    const title = () => {
        const inputTitle = input.title()

        if (inputTitle) {
            return `${inputTitle} • ${t('globals.website_name')}`
        }

        return globalDefault.value
    }

    useSegmentPage(
        computed(() => title()),
        data,
        fetching,
        stale
    )

    const site_name = t('globals.website_name')

    const description = () => {
        let description = input.description

        if (typeof description === 'function') {
            description = description()
        }

        return description || t('globals.default_meta_description')
    }

    const { locale } = useTranslator()

    return useHead(
        {
            title,
            htmlAttrs: {
                lang: () => locale.value,
            },
            meta: () => {
                const desc = description()

                const metaArray: Meta[] = [
                    {
                        name: 'description',
                        content: desc,
                    },
                    {
                        name: 'robots',
                        content: robots.content.value,
                    },
                ]

                if (input.og) {
                    const og = input.og

                    metaArray.push({
                        name: 'og:title',
                        content: getMeta(og.title) || getMeta(title),
                    })
                    metaArray.push({
                        name: 'og:description',
                        content: getMeta(og.description) || desc,
                    })

                    const ogType = getMeta(og.type)

                    if (ogType) {
                        metaArray.push({
                            name: 'og:type',
                            content: ogType,
                        })
                    }

                    const url = getMeta(og.url)
                    if (url) {
                        metaArray.push({
                            name: 'og:url',
                            content:
                                typeof url === 'string'
                                    ? url
                                    : router.resolve(url).fullPath,
                        })
                    }

                    const ogImage = getMeta(og.image)
                    if (ogImage) {
                        metaArray.push({
                            name: 'og:image',
                            content: ogImage,
                        })
                    }

                    metaArray.push({
                        name: 'og:site_name',
                        content: getMeta(og.site_name) || site_name,
                    })
                }

                if (input.article) {
                    const publishedTime = getMeta(input.article.published_time)

                    if (publishedTime) {
                        metaArray.push({
                            name: 'article:published_time',
                            content: publishedTime.toLocaleDateString(),
                        })
                    }

                    const articleAuthor = getMeta(input.article.author)

                    if (articleAuthor) {
                        metaArray.push({
                            name: 'article:author',
                            content: articleAuthor,
                        })
                    }
                }

                return metaArray
            },
        },
        options
    )
}

function getMeta<T>(v: MaybeFunction<T>): T {
    return v instanceof Function ? v() : v
}
